import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, A11y } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

/* ---------------- Data ---------------- */
const SERVICES = [
  {
    key: "buero",
    title: "Büroreinigung",
    blurb: "Saubere Arbeitsflächen & hygienische Sanitärbereiche für produktive Teams.",
    Icon: OfficeIcon,
    tag: "Business",
    recommended: true,
  },
  {
    key: "treppen",
    title: "Treppenhausreinigung",
    blurb: "Sichere, rutschfreie und gepflegte Treppenhäuser – erster Eindruck zählt.",
    Icon: StairsIcon,
    tag: "Haus & Wohnen",
  },
  {
    key: "fenster",
    title: "Fensterreinigung",
    blurb: "Streifenfreie Fenster, Rahmen & Falzen – innen & außen, inkl. Glasfassaden.",
    Icon: WindowIcon,
    tag: "Glas",
  },
  {
    key: "bauend",
    title: "Bauendreinigung",
    blurb: "Nach Bau oder Renovierung: staubfrei, bezugsfertig, gründlich.",
    Icon: ConstructionIcon,
    tag: "Projekt",
  },
  {
    key: "grund",
    title: "Grundreinigung",
    blurb: "Porentiefe Intensivreinigung für hartnäckige Verschmutzungen & Belagpflege.",
    Icon: SparkleIcon,
    tag: "Intensiv",
  },
  {
    key: "hausmeister",
    title: "Hausmeisterdienst",
    blurb: "Regelmäßige Objektbetreuung: Kontrolle, Pflege & Kleinstreparaturen.",
    Icon: ToolsIcon,
    tag: "Facility",
  },
  {
    key: "winter",
    title: "Winterdienst",
    blurb: "Räumen & Streuen von Gehwegen, Zufahrten, Parkplätzen gemäß Vorschrift.",
    Icon: SnowIcon,
    tag: "Saison",
  },
  {
    key: "gruen",
    title: "Grünpflege",
    blurb: "Rasen- & Heckenschnitt, Beetpflege und Laubentfernung – Außenanlagen top.",
    Icon: LeafIcon,
    tag: "Außen",
  },
  {
    key: "umzug",
    title: "Umzugsreinigung",
    blurb: "Übergabereif nach Auszug – Küche, Bad & schwierige Stellen inklusive.",
    Icon: TruckIcon,
    tag: "Umzug",
  },
  {
    key: "gastro",
    title: "Gastronomiereinigung",
    blurb: "HACCP-konforme Reinigung: Küche, Gastraum & Kühlbereiche.",
    Icon: RestaurantIcon,
    tag: "Gastro",
  },
  {
    key: "desinfekt",
    title: "Desinfektionsreinigung",
    blurb: "Gezielte Flächendesinfektion – sensibel, dokumentiert, zuverlässig.",
    Icon: ShieldIcon,
    tag: "Hygiene",
  },
  {
    key: "solar",
    title: "Solaranlagen-Reinigung",
    blurb: "Schonende Reinigung von PV- & Solarmodulen für mehr Effizienz.",
    Icon: SunIcon,
    tag: "Energie",
    badge: "Neu",
  },
  {
    key: "industrie",
    title: "Industriereinigung",
    blurb: "Maschinen & Flächen – sicher, planbar, effizient.",
    Icon: FactoryIcon,
    tag: "Industrie",
  },
];

// Recommended first, the rest alphabetically (clean, curated feel)
const SORTED = [
  ...SERVICES.filter((s) => s.recommended),
  ...SERVICES.filter((s) => !s.recommended).sort((a, b) => a.title.localeCompare(b.title, "de")),
];

/* ---------------- Component ---------------- */
export default function ServicesShowcase() {
  return (
    <section id="services" className="relative bg-white py-16 sm:py-20" aria-labelledby="services-title">
      
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        {/* Header */}
        <header className="text-center">
          <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
            Leistungen
          </span>
          <h2
            id="services-title"
            className="mt-4 text-3xl font-extrabold leading-tight tracking-tight text-gray-900 sm:text-4xl md:text-5xl"
          >
            Professionelle Reinigungs- & Gebäudeservices
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-gray-600 sm:text-base">
            Kuratiert präsentiert – wischen Sie durch unsere wichtigsten Leistungen.
          </p>
        </header>

        {/* Carousel wrapper with edge fades */}
        <div className="relative mt-8">
          {/* Edge fades for premium depth */}
          <div className="pointer-events-none absolute left-0 top-0 z-10 hidden h-full w-10 bg-gradient-to-r from-white to-transparent sm:block" />
          <div className="pointer-events-none absolute right-0 top-0 z-10 hidden h-full w-10 bg-gradient-to-l from-white to-transparent sm:block" />

          <Swiper
            modules={[Autoplay, Pagination, A11y]}
            a11y={{ enabled: true }}
            autoplay={{ delay: 4200, disableOnInteraction: false, pauseOnMouseEnter: true }}
            pagination={{ clickable: true, dynamicBullets: true }}
            spaceBetween={18}
            centeredSlides={false}
            breakpoints={{
              0: { slidesPerView: 1.1, spaceBetween: 14 },
              480: { slidesPerView: 1.4, spaceBetween: 16 },
              640: { slidesPerView: 2, spaceBetween: 18 },
              1024: { slidesPerView: 3, spaceBetween: 20 },
              1280: { slidesPerView: 4, spaceBetween: 22 },
            }}
            className="!pb-12"
          >
            {SORTED.map((card) => (
              <SwiperSlide key={card.key} className="!h-auto">
                <ServiceCard {...card} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Single CTA */}
        <div className="mt-8 text-center">
          <a
            href="#contact"
            className="inline-flex items-center justify-center rounded-full bg-emerald-600 px-6 py-3 text-sm font-semibold text-white shadow hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
          >
            Kontakt aufnehmen
          </a>
        </div>
      </div>

      {/* Premium pagination styling */}
      <style>{`
        .swiper-pagination-bullets {
          margin-top: 8px;
        }
        .swiper-pagination-bullet {
          width: 10px;
          height: 10px;
          background: #e5e7eb; /* gray-200 */
          opacity: 1;
          margin: 0 6px !important;
          transition: all .25s ease;
        }
        .swiper-pagination-bullet:hover {
          background: #9ca3af; /* gray-400 */
        }
        .swiper-pagination-bullet-active {
          width: 26px;
          border-radius: 9999px;
          background: #10b981; /* emerald-500 */
          box-shadow: 0 0 0 3px rgba(16,185,129,.12);
        }
      `}</style>
    </section>
  );
}

/* ---------------- Card ---------------- */
function ServiceCard({ title, blurb, Icon, tag, badge, recommended }) {
  return (
    <article
      className={`relative flex h-full flex-col rounded-2xl border bg-white p-6 shadow-sm transition
      hover:-translate-y-[2px] hover:shadow-md ${recommended ? "border-emerald-300" : "border-gray-100"}`}
    >
      {/* badges line */}
      <div className="mb-4 flex items-center gap-2">
        {recommended && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white shadow">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 12l2 2 4-4" />
              <circle cx="12" cy="12" r="9" />
            </svg>
            Empfohlen
          </span>
        )}
        {badge && (
          <span className="inline-flex rounded-full bg-teal-50 px-2.5 py-1 text-xs font-semibold text-teal-700 ring-1 ring-inset ring-teal-200">
            {badge}
          </span>
        )}
        <span className="ml-auto inline-flex rounded-full bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200">
          {tag}
        </span>
      </div>

      {/* icon + text */}
      <div className="flex items-start gap-3">
        <div className={`inline-flex h-12 w-12 items-center justify-center rounded-xl border text-emerald-600
          ${recommended ? "border-emerald-300 bg-emerald-50" : "border-emerald-200 bg-emerald-50"}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
          <p className="mt-1 text-sm text-gray-600">{blurb}</p>
        </div>
      </div>

      {/* divider */}
      <div className="mt-5 h-px w-full bg-gradient-to-r from-transparent via-emerald-200/70 to-transparent" />

      {/* minimal detail list (purely visual rhythm) */}
      <ul className="mt-4 grid grid-cols-1 gap-1.5 text-xs text-gray-600">
        <li className="flex items-center gap-2">
          <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
          Qualitätssicherung inklusive
        </li>
        <li className="flex items-center gap-2">
          <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
          Flexible Intervalle
        </li>
        <li className="flex items-center gap-2">
          <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
          Fester Ansprechpartner
        </li>
      </ul>
    </article>
  );
}

/* ---------------- Icons (inline SVG, crisp & light) ---------------- */
function StairsIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M8 40h32" />
      <path d="M12 32h8v8M20 24h8v8M28 16h8v8M36 8h4v8" />
    </svg>
  );
}
function OfficeIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="8" y="8" width="16" height="32" rx="2" />
      <rect x="28" y="14" width="12" height="26" rx="2" />
      <path d="M14 16h4M14 22h4M14 28h4" />
    </svg>
  );
}
function WindowIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="8" y="10" width="32" height="28" rx="2" />
      <path d="M24 10v28M8 24h32" />
    </svg>
  );
}
function ConstructionIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M6 36h36" />
      <path d="M12 36V18l12-6 12 6v18" />
      <path d="M20 36V22h8v14" />
    </svg>
  );
}
function SparkleIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M24 6l3 7 7 3-7 3-3 7-3-7-7-3 7-3 3-7z" />
      <path d="M38 30l2 4 4 2-4 2-2 4-2-4-4-2 4-2 2-4z" />
    </svg>
  );
}
function ToolsIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M30 6l4 4-6 6-4-4M20 18L8 30l10 10 12-12" />
      <path d="M28 22l-6-6" />
    </svg>
  );
}
function SnowIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M24 6v36M10 14l28 20M38 14L10 34" />
      <path d="M18 8l6 4 6-4M18 40l6-4 6 4" />
    </svg>
  );
}
function LeafIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10 30c10 6 22 2 28-8 2-4 2-8 0-12-4 2-8 2-12 0-10 6-14 18-8 28" />
      <path d="M10 30c8-2 14-8 18-14" />
    </svg>
  );
}
function TruckIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="6" y="16" width="22" height="14" rx="2" />
      <path d="M28 20h8l6 6v4h-6" />
      <circle cx="16" cy="34" r="3" />
      <circle cx="34" cy="34" r="3" />
    </svg>
  );
}
function RestaurantIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 6v18M18 6v18M15 24v18" />
      <path d="M30 6c4 0 6 3 6 6s-2 6-6 6v24" />
    </svg>
  );
}
function ShieldIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M24 6l16 6v10c0 10-7 16-16 20C15 38 8 32 8 22V12l16-6z" />
      <path d="M18 24l4 4 8-8" />
    </svg>
  );
}
function SunIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="24" cy="24" r="8" />
      <path d="M24 6v6M24 36v6M6 24h6M36 24h6M11 11l4 4M33 33l4 4M11 37l4-4M33 15l4-4" />
    </svg>
  );
}
function FactoryIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="6" y="20" width="36" height="20" rx="2" />
      <path d="M14 20V8h6v12M26 20v-6l8 6M12 30h6M22 30h6M32 30h6" />
    </svg>
  );
}
